import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from '../auth/AuthContext';

/**
 * Banner global de trial / cobrança.
 *
 * Lê `GET /me/subscription/status` e exibe um aviso conforme o estado:
 *
 *   - `active`              → não renderiza nada (assinante feliz).
 *   - `cancelled`           → não renderiza (ou exibe banner cinza? por
 *                              hora, só exibimos enquanto há ação a
 *                              tomar — cancelado é estado terminal).
 *   - `trial` com >3 dias   → banner azul, info "termina em X dias".
 *   - `trial` com ≤3 dias   → banner amarelo, urgência.
 *   - `trial` expirado      → banner vermelho.
 *   - `past_due`            → banner vermelho com CTA forte.
 *
 * CTA leva pra `/configuracoes?section=assinatura` onde o usuário
 * escolhe plano e dispara checkout.
 */

interface SubscriptionStatus {
  planCode: string;
  status: 'trial' | 'active' | 'past_due' | 'cancelled' | 'pending_payment';
  monthlyPriceCents: number;
  currentPeriodEnd: string | null;
  daysLeft: number | null;
  isTrialExpired: boolean;
  isPaymentRequired: boolean;
}

type Variant = 'info' | 'warn' | 'danger';

const VARIANT_CLASSES: Record<Variant, string> = {
  info: 'bg-blue-50 border-blue-200 text-blue-900',
  warn: 'bg-amber-50 border-amber-200 text-amber-900',
  danger: 'bg-rose-50 border-rose-200 text-rose-900',
};

const VARIANT_BUTTON: Record<Variant, string> = {
  info: 'bg-blue-600 hover:bg-blue-700 text-white',
  warn: 'bg-amber-600 hover:bg-amber-700 text-white',
  danger: 'bg-rose-600 hover:bg-rose-700 text-white',
};

export function TrialBanner() {
  const { user } = useAuth();
  const isE2E =
    typeof navigator !== 'undefined' && navigator.webdriver === true;
  // Owner e manager veem (manager negocia plano em clínicas grandes).
  const canSee = user?.role === 'owner' || user?.role === 'manager';

  const { data } = useQuery<SubscriptionStatus>({
    queryKey: ['subscription.status'],
    queryFn: async () =>
      (await api.get('/me/subscription/status')).data,
    refetchOnWindowFocus: !isE2E,
    staleTime: 60_000,
    retry: 1,
    enabled: canSee && !isE2E,
  });

  if (!canSee || isE2E || !data) return null;

  // Estados que não precisam de banner
  if (data.status === 'active') return null;
  if (data.status === 'cancelled') return null;
  if (data.status === 'pending_payment') return null;

  let variant: Variant = 'info';
  let message = '';
  let ctaLabel = 'Assinar agora';

  if (data.status === 'past_due') {
    variant = 'danger';
    message =
      'Pagamento pendente — assine pra continuar usando o Kevins Clínicas sem interrupções.';
    ctaLabel = 'Regularizar pagamento';
  } else if (data.status === 'trial') {
    const days = data.daysLeft ?? 0;
    if (data.isTrialExpired || days <= 0) {
      variant = 'danger';
      message =
        'Seu trial expirou. Assine agora pra manter acesso à plataforma.';
      ctaLabel = 'Assinar agora';
    } else if (days <= 3) {
      variant = 'warn';
      message =
        days === 1
          ? 'Atenção! Seu trial termina amanhã. Garanta sua assinatura.'
          : `Atenção! Seu trial termina em ${days} dias. Garanta sua assinatura.`;
      ctaLabel = 'Assinar agora';
    } else {
      variant = 'info';
      message = `Seu trial termina em ${days} dias. Aproveite e assine quando quiser — sem interrupção.`;
      ctaLabel = 'Assinar agora';
    }
  }

  return (
    <div
      className={`border-b ${VARIANT_CLASSES[variant]}`}
      data-testid="trial-banner"
      data-variant={variant}
      data-status={data.status}
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm font-medium flex-1 min-w-0">{message}</p>
        <Link
          to="/configuracoes?section=assinatura"
          className={`inline-flex items-center px-3 py-1.5 rounded-md text-xs font-semibold shadow-sm transition-colors ${VARIANT_BUTTON[variant]}`}
          data-testid="trial-banner-cta"
        >
          {ctaLabel}
        </Link>
      </div>
    </div>
  );
}
